import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { WalletConnector } from '@dcentralab/web3-wallet-connector';
import { Header as DUBHeader } from '@dcentralab/unified-frontend';
import { openSignUpModal, openLoginModal } from '../../redux/actions/modalActions';
import { logoutChampionAction } from '../../redux/actions/championActions';
import { CONNECTED_CHAMPION_SELECT_VALUES, NOT_CONNECTED_CHAMPION_SELECT_VALUES } from '../../constants/champion';
import HordButtons from './HordButtons/HordButtons';
import LogoIcon from '../Common/Icons/LogoIcon';
import translate from '../../services/translate/translate';
import { createNotification } from '../../redux/actions/uiActions';
import networks from '../../config/networks.json';
import {
  onWalletConnectSuccessAction,
  onWalletDisconnectAction,
  onWalletConnectRequestAction,
  onWalletConnectErrorAction,
  normalLogin,
  switchNetwork,
} from '../../redux/actions/walletActions';
import CreatePoolButton from './CreatePoolButton/CreatePoolButton';
import config from '../../config/config.json';
import { CustomLink } from '../Common';
import { getStoredChainId, storeNetworkInfo } from '../../services/walletService';

import { eventSetup } from '../../utils/events';
import { walletEvents } from '../../constants/wallet';
import './Header.scss';

const storedChainId = getStoredChainId();

const Header = ({
  championData,
  openSignUpModal,
  openLoginModal,
  logoutChampionAction,
  onWalletConnectSuccess,
  onWalletDisconnect,
  onWalletConnectRequest,
  onWalletConnectError,
  account,
  normalLogin,
  switchNetwork,
}) => {
  const history = useHistory();
  const ref = useRef(null);

  const { pathname } = useLocation();
  const [selectedNetwork, setSelectedNetwork] = useState(+storedChainId || config.network);

  const closeMenu = useCallback(() => {
    ref?.current?.();
  }, [ref]);

  useEffect(() => {
    closeMenu();
  }, [pathname, history.action]);

  useEffect(() => {
    storeNetworkInfo(selectedNetwork);
  }, [selectedNetwork]);

  const updateNetworkInfo = (network) => {
    switchNetwork(network);
    storeNetworkInfo(network);
    setSelectedNetwork(network);
  };

  const onNetworkSelect = useCallback(
    (network) => {
      updateNetworkInfo(network);
      if (!account) {
        normalLogin();
      }
    },
    [account, normalLogin],
  );

  const logo = useMemo(
    () => ({
      text: 'APP',
      to: '/',
      img: <LogoIcon />,
    }),
    [],
  );

  const menuLinks = useMemo(() => {
    if (championData) {
      return CONNECTED_CHAMPION_SELECT_VALUES.map((option) => {
        let onClick = option.onClick;
        if (!onClick) {
          if (option.value === 'profile') {
            onClick = () => {
              history.push(`/champion/${championData.handle}`);
            };
          }
          if (option.value === 'logout') {
            onClick = () => {
              logoutChampionAction(window.location.pathname, history);
            };
          }
        }

        return { onClick, text: translate(option.label), icon: option.icon, href: option.href };
      });
    }
    return NOT_CONNECTED_CHAMPION_SELECT_VALUES.map((option) => {
      let onClick = option.onClick;
      let href = option.href;
      if (!onClick) {
        if (option.value === 'login') onClick = openLoginModal;
        if (option.value === 'signup') onClick = openSignUpModal;
      }
      if (!href && option.value === 'apply') {
        href = 'https://dcentralab.typeform.com/to/Jp5jmOcy';
      }
      return { text: translate(option.label), icon: option.icon, href, onClick };
    });
  }, [championData, openLoginModal, openSignUpModal, logoutChampionAction, history]);

  const links = useMemo(
    () => [
      {
        href: config.dexUrl,
        text: 'Dex Marketplace',
      },
    ],
    [],
  );

  const networkSelectorProps = useMemo(
    () => ({
      networks: networks.networks,
      networkId: selectedNetwork,
      account,
      onNetworkSelect,
    }),
    [account, onNetworkSelect, selectedNetwork],
  );

  useEffect(() => {
    eventSetup.on(walletEvents.NETWORK_CHANGE_REQUIRED, ({ netId }) => {
      onNetworkSelect(netId);
    });
  }, []);

  return (
    <DUBHeader
      logo={logo}
      links={links}
      menuLinks={menuLinks}
      networkSelectorProps={networkSelectorProps}
      profileImage={championData?.profile_media_url}
      onCloseCallbackRef={ref}
      walletComponent={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <WalletConnector
          networkId={config.network}
          rpcUrl={config.rpcUrl}
          onConnectRequest={onWalletConnectRequest}
          onConnect={onWalletConnectSuccess}
          onConnectError={onWalletConnectError}
          onDisconnect={onWalletDisconnect}
          autoConnectInPageProvider
          showBalance
        />
      }
    >
      <div>
        <CreatePoolButton>
          <CustomLink to="/create-pool/choose-pool-type" text="pools.create_hpool" />
        </CreatePoolButton>
      </div>
      <HordButtons />
    </DUBHeader>
  );
};

Header.defaultProps = {
  championData: null,
  account: null,
};

Header.propTypes = {
  account: PropTypes.string,
  championData: PropTypes.object,
  openLoginModal: PropTypes.func.isRequired,
  openSignUpModal: PropTypes.func.isRequired,
  logoutChampionAction: PropTypes.func.isRequired,
  // isChampionVerified: PropTypes.bool.isRequired,
  onWalletConnectSuccess: PropTypes.func.isRequired,
  onWalletDisconnect: PropTypes.func.isRequired,
  onWalletConnectRequest: PropTypes.func.isRequired,
  onWalletConnectError: PropTypes.func.isRequired,
  normalLogin: PropTypes.func.isRequired,
  switchNetwork: PropTypes.func.isRequired,
};

const mapStateToProps = ({ champion, wallet }) => ({
  championData: champion.championData,
  // isChampionVerified: champion.championData?.is_verified_champion === 'VERIFIED',
  account: wallet.account,
});

const mapDispatchToProps = {
  openLoginModal,
  openSignUpModal,
  createNotification,
  onWalletConnectSuccess: onWalletConnectSuccessAction,
  onWalletDisconnect: onWalletDisconnectAction,
  onWalletConnectRequest: onWalletConnectRequestAction,
  onWalletConnectError: onWalletConnectErrorAction,
  normalLogin,
  logoutChampionAction,
  switchNetwork,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
